import { generateUniqueId } from './generateUniqueId';
import { IDataItem } from '../../store/ducks/ClientReq/types';

const getPayloadKeyFromFilter = (filterKey: string): string | null => {
  const keyMapping: { [key: string]: string } = {
    group_id: 'group',
    division_id: 'division',
    region_id: 'region',
    legal_entity_id: 'legal_entity',
    business_unit_id: 'business_unit',
    industryId: 'industry_id'
  };

  return keyMapping[filterKey] || null;
};

const isValidBruId = (item: IDataItem): boolean => {
  return (
    item.bru_id !== null &&
    item.bru_id !== undefined &&
    !isNaN(Number(item.bru_id)) &&
    Number(item.bru_id) > 0
  );
};

const createBasePayload = (
  item: IDataItem,
  isUpdate: boolean,
  industryId?: number,
  moduleId?: number,
  options?: { generateBruId?: boolean }
) => ({
  bru_id: isUpdate
    ? Number(item.bru_id)
    : item.bru_id || (options?.generateBruId ? generateUniqueId() : item.id),
  ...(item.aur_parent_key && { aur_parent_key: item.aur_parent_key }),
  ...(item.aur_child_key && { aur_child_key: item.aur_child_key }),
  ...(item.value !== undefined && { value: item.value }),
  ...(item.module_id !== undefined && { module_id: Number(item.module_id) }),
  ...(item.module_id === undefined &&
    moduleId !== undefined && { module_id: Number(moduleId) }),
  ...(item.aur_id !== undefined && {
    aur_id: isUpdate ? Number(item.aur_id) : item.aur_id
  }),
  ...(item.group !== undefined && {
    group: isUpdate ? Number(item.group) : item.group
  }),
  ...(isUpdate &&
    item.industry_id !== undefined && {
      industry_id: Number(item.industry_id)
    }),
  ...(!isUpdate && industryId !== undefined && { industry_id: industryId }),
  ...(item.category !== undefined &&
    item.category !== null && {
      category: isUpdate ? Number(item.category) : item.category
    })
});

const createFilterPayload = (filters: any, isUpdate: boolean) => {
  return Object.keys(filters).reduce((acc, filterKey) => {
    if (filterKey === 'industryId' || filterKey === 'moduleId') return acc;

    const filterValue = filters[filterKey]?.[0];
    if (filterValue !== null && filterValue !== undefined) {
      const payloadKey = getPayloadKeyFromFilter(filterKey);
      if (payloadKey) acc[payloadKey] = filterValue;
    }
    return acc;
  }, {} as any);
};

const generateCombinations = (industryIds: number[], moduleIds: number[]) => {
  const combinations = [];
  for (const industryId of industryIds) {
    for (const moduleId of moduleIds) {
      combinations.push({ industryId, moduleId });
    }
  }
  return combinations;
};

export const applyFiltersToData = (
  data: IDataItem[],
  filters: any,
  options?: { generateBruId?: boolean }
) => {
  const industryIds = filters.industryId || [];
  const moduleIds = filters.moduleId || [];
  const result = [];

  for (const item of data) {
    const isUpdate = isValidBruId(item) && !options?.generateBruId;
    const filterPayload = createFilterPayload(filters, isUpdate);

    if (isUpdate) {
      const moduleId =
        item.module_id || moduleIds.find((id: number) => id !== undefined);
      const industryId =
        item.industry_id || industryIds.find((id: number) => id !== undefined);

      const basePayload = createBasePayload(
        item,
        true,
        industryId,
        moduleId,
        options
      );
      result.push({ ...basePayload, ...filterPayload });
    } else {
      const isCategoryOnly = item.aur_id && !item.bru_id && !item.value?.trim();

      if (isCategoryOnly) {
        const basePayload = createBasePayload(
          item,
          false,
          industryIds.find((id: number) => id !== undefined),
          moduleIds.find((id: number) => id !== undefined),
          options
        );
        result.push({ ...basePayload, ...filterPayload });
      } else {
        const combinations = generateCombinations(industryIds, moduleIds);
        for (const { industryId, moduleId } of combinations) {
          const basePayload = createBasePayload(
            item,
            false,
            industryId,
            moduleId,
            options
          );
          result.push({ ...basePayload, ...filterPayload });
        }
      }
    }
  }

  return result;
};

export const useFilterMapping = () => {
  return {
    applyFiltersToData,
    getPayloadKeyFromFilter,
    isValidBruId
  };
};
